import React, { useEffect, useState } from "react";

import { Divider, Typography, Grid } from "@mui/material";

import {
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

import moment from "moment";

export default function ExternalReport({
  data,
  dataHospitals,
  selectedHospitals,
  selectedHospitalsOfOrigins,
  fromDate,
  toDate,
  ...props
}) {
  const [dataGrouped, setDataGrouped] = useState([]);

  const [dataBedOccupancy, setDataBedOccupancy] = useState([]);

  useEffect(() => {
    const filteredData =
      selectedHospitalsOfOrigins.length > 0
        ? data.filter((patient) =>
            selectedHospitalsOfOrigins.includes(patient.hospital_of_origin_id)
          )
        : data;

    const groupedData = filteredData.reduce((acc, patient) => {
      const hospitalOfOriginId = patient.hospital_of_origin_id;
      const hospitalOfOriginName = patient.hospital_of_origin_name;

      let group = acc.find((group) => group.id === hospitalOfOriginId);

      if (!group) {
        group = {
          id: hospitalOfOriginId,
          name: hospitalOfOriginName,
          patients: [],
        };
        acc.push(group);
      }

      group.patients.push(patient);

      return acc;
    }, []);

    setDataGrouped(groupedData);
  }, [data]);

  useEffect(() => {
    calculateBedOccupancyData();
  }, [data, dataHospitals, selectedHospitals]);

  const isNullOrEmpty = (value) => {
    if (value === null || value === undefined) {
      return true;
    }

    if (typeof value === "string" && value.trim().toLowerCase() === "null") {
      return true;
    }

    if (Array.isArray(value) && value.length === 0) {
      return true;
    }

    if (typeof value === "string" && value.trim() === "") {
      return true;
    }

    if (typeof value === "number" && isNaN(value)) {
      return true;
    }

    return false;
  };

  const formatLabelDate = (value) =>
    new Date(value).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const generateBedAvailabilityGraph = (name, patientList) => {
    const filteredHospital = dataHospitals.find((item) => item.name === name);

    if (!filteredHospital) {
      return null;
    }

    let totalBeds = filteredHospital.total_beds;
    let tciCount = filteredHospital.tci_count;

    const bedOccupancy = {};

    patientList.forEach((patient) => {
      const admissionDate = new Date(
        JSON.parse(patient.patient_details).admitted_date
      );
      const dischargeDate = JSON.parse(patient.patient_details).discharge_date
        ? new Date(JSON.parse(patient.patient_details).discharge_date)
        : new Date();

      const currentDate = admissionDate;

      while (currentDate <= dischargeDate) {
        const dateKey = currentDate.toISOString().split("T")[0];

        if (!bedOccupancy[dateKey]) {
          bedOccupancy[dateKey] = 0;
        }

        bedOccupancy[dateKey]++;
        currentDate.setDate(currentDate.getDate() + 1);
      }
    });

    const bedAvailabilityData = [];

    Object.keys(bedOccupancy).forEach((date) => {
      bedAvailabilityData.push({
        date,
        Occupied: bedOccupancy[date],
        Available: totalBeds - bedOccupancy[date],
        TCI: tciCount,
      });
    });

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          width: "100%",
          height: 450,
        }}
      >
        <Typography
          variant="subtitle2"
          align="center"
          style={{ whiteSpace: "nowrap" }}
        >
          Beds Availability
        </Typography>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            width={730}
            height={250}
            data={bedAvailabilityData}
            margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              //scale="time"
              tickFormatter={(date) => moment(date).format("DD-MMM")}
            />
            <YAxis />
            <Tooltip
              labelFormatter={(value) => moment(value).format("DD/MM/YYYY")}
            />
            <Legend align="center" verticalAlign="bottom" height={36} />
            <Bar dataKey="Available" fill="#4472c4" stackId="stack" />
            <Bar dataKey="TCI" fill="navy" stackId="stack" />
            <Bar dataKey="Occupied" fill="#ed7d30" stackId="stack" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const generateEDDComplianceGraph = (patientList) => {
    const data = [
      { name: "On Time", value: 0, color: "#548235" },
      { name: "1 day", value: 0, color: "#548235" },
      { name: "2 days", value: 0, color: "#ffd965" },
      { name: "3 days", value: 0, color: "#ff0201" },
      { name: ">= 4 days", value: 0, color: "#ff0201" },
    ];

    patientList.forEach((patient) => {
      const patientDetails = JSON.parse(patient.patient_details);

      if (patientDetails.edd_alignment === 0) {
        data[0].value++;
      } else if (patientDetails.edd_alignment === 1) {
        data[1].value++;
      } else if (patientDetails.edd_alignment === 2) {
        data[2].value++;
      } else if (patientDetails.edd_alignment === 3) {
        data[3].value++;
      } else if (patientDetails.edd_alignment >= 4) {
        data[4].value++;
      }
    });

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          width: "100%",
          height: 450,
        }}
      >
        <Typography
          variant="subtitle2"
          align="center"
          style={{ whiteSpace: "nowrap" }}
        >
          EDD Compliance
        </Typography>

        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            width={730}
            height={250}
            data={data}
            margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis domain={[0, (dataMax) => dataMax + 1]} interval={1} />
            <Tooltip />

            <Bar dataKey="value" label={{ position: "top" }}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const generateGenderGraph = (patientList) => {
    let maleCount = 0;
    let femaleCount = 0;
    let unknownCount = 0;

    patientList.forEach((patient) => {
      const patientDetails = JSON.parse(patient.patient_details);

      if (patientDetails.gender === "m") {
        maleCount++;
      } else if (patientDetails.gender === "f") {
        femaleCount++;
      } else if (patientDetails.gender === "u") {
        unknownCount++;
      }
    });

    const COLORS = ["#0088FE", "#FF69B4", "#757575"];

    const totalPatients = maleCount + femaleCount + unknownCount;

    const data = [
      {
        name: "Male",
        value: maleCount,
        percentage: (maleCount / totalPatients) * 100,
      },
      {
        name: "Female",
        value: femaleCount,
        percentage: (femaleCount / totalPatients) * 100,
      },
      {
        name: "Unknown",
        value: unknownCount,
        percentage: (unknownCount / totalPatients) * 100,
      },
    ];

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          width: 350,
          height: 300,
        }}
      >
        <Typography
          variant="subtitle2"
          align="center"
          style={{ whiteSpace: "nowrap" }}
        >
          Bed Usage By Sex
        </Typography>
        <PieChart width={350} height={300}>
          <Pie
            dataKey="value"
            data={data}
            cx={"50%"}
            cy={"50%"}
            outerRadius={80}
            fill="#8884d8"
            label={({
              cx,
              cy,
              midAngle,
              innerRadius,
              outerRadius,
              percent,
              value,
              index,
            }) => {
              const RADIAN = Math.PI / 180;

              const radius = 25 + innerRadius + (outerRadius - innerRadius);
              const x = cx + radius * Math.cos(-midAngle * RADIAN);
              const y = cy + radius * Math.sin(-midAngle * RADIAN);

              return (
                <text
                  x={x}
                  y={y}
                  fill={COLORS[index % COLORS.length]}
                  textAnchor={x > cx ? "start" : "end"}
                  dominantBaseline="central"
                >
                  {data[index].percentage.toFixed(0) +
                    "% (" +
                    data[index].value +
                    ")"}
                </text>
              );
            }}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </Pie>
          <Legend
            align="center"
            verticalAlign="bottom"
            height={36}
            payload={data.map((item, index) => ({
              id: item.name,
              type: "square",
              value: `${item.name} - ${item.percentage.toFixed(0)}% (${
                item.value
              })`,
              color: COLORS[index % COLORS.length],
            }))}
          />

          <Tooltip />
        </PieChart>
      </div>
    );
  };

  const generateStatusGraph = (patientList) => {
    let admitted = 0;
    let discharged = 0;

    patientList.forEach((patient) => {
      const patientDetails = JSON.parse(patient.patient_details);

      if (patientDetails.status === 1) {
        discharged++;
      } else if (patientDetails.status === 0) {
        admitted++;
      }
    });

    const COLORS = ["#11cb5f", "#C70039"];

    const totalPatients = admitted + discharged;

    const data = [
      {
        name: "Active",
        value: admitted,
        percentage: (admitted / totalPatients) * 100,
      },
      {
        name: "Discharged",
        value: discharged,
        percentage: (discharged / totalPatients) * 100,
      },
    ];

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          width: 350,
          height: 300,
        }}
      >
        <Typography
          variant="subtitle2"
          align="center"
          style={{ whiteSpace: "nowrap" }}
        >
          Patients Under Care
        </Typography>
        <PieChart width={350} height={300}>
          <Pie
            dataKey="value"
            data={data}
            cx={"50%"}
            cy={"50%"}
            outerRadius={80}
            fill="#8884d8"
            label={({
              cx,
              cy,
              midAngle,
              innerRadius,
              outerRadius,
              percent,
              value,
              index,
            }) => {
              const RADIAN = Math.PI / 180;

              const radius = 25 + innerRadius + (outerRadius - innerRadius);
              const x = cx + radius * Math.cos(-midAngle * RADIAN);
              const y = cy + radius * Math.sin(-midAngle * RADIAN);

              return (
                <text
                  x={x}
                  y={y}
                  fill={COLORS[index % COLORS.length]}
                  textAnchor={x > cx ? "start" : "end"}
                  dominantBaseline="central"
                >
                  {data[index].percentage.toFixed(0) +
                    "% (" +
                    data[index].value +
                    ")"}
                </text>
              );
            }}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </Pie>
          <Legend
            align="center"
            verticalAlign="bottom"
            height={36}
            payload={data.map((item, index) => ({
              id: item.name,
              type: "square",
              value: `${item.name} - ${item.percentage.toFixed(0)}% (${
                item.value
              })`,
              color: COLORS[index % COLORS.length],
            }))}
          />
          <Tooltip />
        </PieChart>
      </div>
    );
  };

  const generatePatientStayDurationGraph = (patientList) => {
    const calculateAverageStayDuration = () => {
      const totalStayDuration = patientList.reduce(
        (acc, patient) => acc + JSON.parse(patient.patient_details).stay_length,
        0
      );
      return totalStayDuration / patientList.length;
    };

    const averageStayDuration = calculateAverageStayDuration();

    const stayData = patientList.map((patient) => {
      const patientDetails = JSON.parse(patient.patient_details);
      return { stay_length: patientDetails.stay_length };
    });

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          width: "100%",
          height: 450,
        }}
      >
        <Typography
          variant="subtitle2"
          align="center"
          style={{ whiteSpace: "nowrap" }}
        >
          Patient Stay Duration
        </Typography>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            width={730}
            height={250}
            data={stayData}
            margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar
              dataKey="stay_length"
              fill="#8884d895"
              name="Stay Duration"
              label={{ position: "top" }}
            />
            <ReferenceLine
              y={averageStayDuration}
              stroke="red"
              label={{
                position: "top",
                value: `Average: ${averageStayDuration.toFixed(2)} days`,
                fill: "red",
                fontSize: 14,
              }}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const generatePatientAverageAgeGraph = (patientList) => {
    const calculateAverageAge = () => {
      const totalAge = patientList.reduce((acc, patient) => {
        const dobString = JSON.parse(patient.patient_details).dob;
        const dobMoment = moment(dobString, "YYYY-MM-DD");
        const age = moment().diff(dobMoment, "years");
        return acc + age;
      }, 0);
      return totalAge / patientList.length;
    };

    const averageAge = calculateAverageAge();

    const ageData = patientList.map((patient) => {
      const dobString = JSON.parse(patient.patient_details).dob;
      const dobMoment = moment(dobString, "YYYY-MM-DD");
      const age = moment().diff(dobMoment, "years");
      return { name: patient.name, age };
    });

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          width: "100%",
          height: 450,
        }}
      >
        <Typography
          variant="subtitle2"
          align="center"
          style={{ whiteSpace: "nowrap" }}
        >
          Patient Average Age
        </Typography>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            width={730}
            height={250}
            data={ageData}
            margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />

            <Bar
              dataKey="age"
              fill="#8884D495"
              name="Age"
              label={{ position: "top" }}
            />

            <ReferenceLine
              y={averageAge}
              stroke="red"
              label={{
                position: "top",
                value: `Average: ${averageAge.toFixed(2)} years`,
                fill: "red",
                fontSize: 14,
              }}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderBedOccupancy = (item) => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          width: "100%",
          height: 450,
        }}
      >
        <Typography
          variant="subtitle2"
          align="center"
          style={{ whiteSpace: "nowrap" }}
        >
          {item.name + " Beds Availability"}
        </Typography>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            width={730}
            height={250}
            data={item.bedAvailabilityData}
            margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              //scale="time"
              tickFormatter={(date) => moment(date).format("DD-MMM")}
            />
            <YAxis />
            <Tooltip
              labelFormatter={(value) => moment(value).format("DD/MM/YYYY")}
            />
            <Legend align="center" verticalAlign="bottom" height={36} />
            <Bar dataKey="Available" fill="#4472c4" stackId="stack" />
            <Bar dataKey="TCI" fill="navy" stackId="stack" />
            <Bar dataKey="Occupied" fill="#ed7d30" stackId="stack" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const calculateBedOccupancyData = () => {
    if (
      dataHospitals !== null &&
      dataHospitals.length > 0 &&
      selectedHospitals !== null &&
      selectedHospitals.length > 0
    ) {
      const fromDateMoment = moment(fromDate);
      const toDateMoment = moment(toDate);

      const dateRange = [];
      const currentDate = fromDateMoment.clone();
      while (currentDate.isSameOrBefore(toDateMoment, "day")) {
        dateRange.push(currentDate.format("YYYY-MM-DD"));
        currentDate.add(1, "day");
      }

      const filteredData =
        selectedHospitals.length > 0
          ? dataHospitals.filter((item) => selectedHospitals.includes(item.id))
          : data;

      let finalData = [];

      filteredData.forEach((hospital) => {
        let totalBeds = hospital.total_beds;
        let name = hospital.name;

        let bedAvailabilityData = [];

        dateRange.forEach((date) => {
          let occupiedCount = 0;
          let tciCount = 0;

          data.forEach((patient) => {
            const patientDetails = JSON.parse(patient.patient_details);

            if (
              patientDetails.latest_eoc_event_type === "scheduled" &&
              patient.hospital_name === name
            ) {
              const scheduledDate = patientDetails.scheduled_date
                ? moment(patientDetails.scheduled_date).format("YYYY-MM-DD")
                : null;

              if (
                moment(date).isSameOrAfter(scheduledDate, "day") &&
                patient.hospital_name === name
              ) {
                tciCount++;
              }
            } else {
              const admittedDate = moment(patientDetails.admitted_date).format(
                "YYYY-MM-DD"
              );
              const dischargeDate = patientDetails.discharge_date
                ? moment(patientDetails.discharge_date).format("YYYY-MM-DD")
                : null;

              if (
                moment(date).isSameOrAfter(admittedDate, "day") &&
                (dischargeDate === null ||
                  moment(date).isBefore(dischargeDate, "day")) &&
                patient.hospital_name === name
              ) {
                occupiedCount++;
              }
            }
          });

          bedAvailabilityData.push({
            date,
            Occupied: occupiedCount,
            Available: totalBeds - occupiedCount,
            TCI: tciCount,
          });
        });

        finalData.push({
          name,
          bedAvailabilityData: bedAvailabilityData,
        });
      });

      setDataBedOccupancy(finalData);
    } else {
      setDataBedOccupancy([]);
    }
  };

  return (
    <>
      <Grid
        container
        spacing={1}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {dataGrouped !== null && dataGrouped.length > 0 ? (
          <>
            {dataBedOccupancy !== null &&
              dataBedOccupancy.length > 0 &&
              dataBedOccupancy.map((dataItem) => renderBedOccupancy(dataItem))}
            {dataGrouped.map((dataItem) => (
              <Grid item xs={12} md={12} style={{ margin: 10 }}>
                <Typography variant="h6" style={{}}>
                  {dataItem.name}
                </Typography>
                <Divider
                  orientation="horizontal"
                  variant="fullWidth"
                  style={{ padding: 2, marginBottom: 15 }}
                />

                {/* <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    paddingTop: 40,
                    //pageBreakBefore: "always",
                  }}
                >
                  {generateBedAvailabilityGraph(
                    dataItem.hospital_name,
                    dataItem.patients
                  )}
                </div> */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    paddingTop: 40,
                    pageBreakBefore: "always",
                  }}
                >
                  {generateEDDComplianceGraph(dataItem.patients)}
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-around",
                    paddingTop: 40,
                    pageBreakBefore: "always",
                  }}
                >
                  {generateGenderGraph(dataItem.patients)}
                  {generateStatusGraph(dataItem.patients)}
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    paddingTop: 40,
                    pageBreakBefore: "always",
                  }}
                >
                  {generatePatientStayDurationGraph(dataItem.patients)}
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    paddingTop: 40,
                    pageBreakBefore: "always",
                  }}
                >
                  {generatePatientAverageAgeGraph(dataItem.patients)}
                </div>
              </Grid>
            ))}
          </>
        ) : (
          data === null && (
            <Typography
              data-test={"reports-empty-msg"}
              style={{ textAlign: "center" }}
            >
              There is no data available for the selected ranges.
            </Typography>
          )
        )}
      </Grid>
    </>
  );
}
